import { createContext, useContext, useMemo, useRef, useState, useEffect } from "react";

const VideoEditorPlaybackClockContext = createContext(null);

const FALLBACK_CLOCK = {
  get: () => 0,
  set: () => {},
  subscribe: () => () => {},
};

export const VideoEditorPlaybackClockProvider = ({ children }) => {
  const timeRef = useRef(0);
  const listenersRef = useRef(new Set());
  const api = useMemo(
    () => ({
      get: () => timeRef.current,
      set: (next) => {
        const value = Math.max(0, Number(next) || 0);
        if (value === timeRef.current) {
          return;
        }
        timeRef.current = value;
        listenersRef.current.forEach((listener) => listener(value));
      },
      subscribe: (listener) => {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
      },
    }),
    []
  );

  return (
    <VideoEditorPlaybackClockContext.Provider value={api}>
      {children}
    </VideoEditorPlaybackClockContext.Provider>
  );
};

// eslint-disable-next-line
export const useVideoEditorPlaybackClock = () =>
  useContext(VideoEditorPlaybackClockContext) || FALLBACK_CLOCK;

// eslint-disable-next-line
export const useVideoEditorPlaybackTime = (fallback = 0) => {
  const clock = useVideoEditorPlaybackClock();
  const [time, setTime] = useState(() => clock.get() ?? fallback);

  useEffect(() => {
    setTime(clock.get() ?? fallback);
    return clock.subscribe(setTime);
  }, [clock, fallback]);

  return time;
};
